import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, message } from 'antd'
import { useSelector } from 'react-redux'
import './index.css'

import PageHeader from './components/PageHeader'
import OrgTabs from './components/OrgTabs'
import Toolbar from './components/Toolbar'
import AllOrganizationsMembers from './components/AllOrganizationsMembers'
import OrganizationMembers from './components/OrganizationMembers'
import MemberTableState from './components/MemberTableState'
import AssignOrgModal from './components/modals/AssignOrgModal'
import InviteUserModal from './components/modals/InviteUserModal'
import EditUserModal from './components/modals/EditUserModal'
import UpgradeToBusinessModal from '../../UpgradeToBusinessModal'

import type {
  AssignmentRecord,
  EditUserPayload,
  InvitePayload,
  Member,
  MemberListFilters,
  Organization,
  OrgRole,
  OrgSelection,
  OrgTabId,
  UserManagementProps,
} from './types'
import { EMPTY_MEMBER_FILTERS } from './types'
import type { NewOrganizationResult } from './components/NewOrganization'
import {
  assignManagedOrganizations,
  checkManagedOrganizationInvite,
  disableManagedOrganizationLoanOfficer,
  enableManagedOrganizationLoanOfficer,
  fetchManagedOrganizationAssignments,
  fetchManagedOrganizations,
  resendManagedOrganizationInvite,
  sendManagedOrganizationInvite,
  updateManagedOrganizationLoanOfficer,
} from './api/organization'
import {
  buildOrgSelections,
  canAddUsersToOrganization,
  formatInviteFailureMessage,
  hasSuperAdminRole,
  organizationPlan,
  protectedRolesFromList,
  resolveManagedOrgTab,
  rolesFromSelections,
  sortOrganizationsByName,
} from './utils'
import { getCustomerId } from '../../../Common'
import { sendSignupToHubSpot } from '../../../signup/hubspotSignup'
import { resolveHubSpotCompanyAsync } from '../../../signup/subscriptionCompany'
import { track } from 'statsig'
import { STATSIG, PAGE } from 'statsig/events'

const UserManagement: React.FC<UserManagementProps> = ({ toPricing }) => {
  const subscription = useSelector((state: any) => state.subscriptionReducer.currentInfo)
  const currentCustomerId = getCustomerId()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [canCreateOrganization, setCanCreateOrganization] = useState(false)
  const [loadingOrganizations, setLoadingOrganizations] = useState(true)
  const [orgTab, setOrgTab] = useState<OrgTabId>('all')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<MemberListFilters>(EMPTY_MEMBER_FILTERS)
  const [querySearch, setQuerySearch] = useState('')
  const [queryFilters, setQueryFilters] = useState<MemberListFilters>(EMPTY_MEMBER_FILTERS)

  const [assignMember, setAssignMember] = useState<Member | null>(null)
  const [assignSourceOrganization, setAssignSourceOrganization] = useState<Organization | null>(null)
  const [assignSelections, setAssignSelections] = useState<Record<string, OrgSelection>>({})
  const [assignExistingRolesByOrg, setAssignExistingRolesByOrg] = useState<Record<string, OrgRole[]>>({})
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignSubmitting, setAssignSubmitting] = useState(false)
  const assignRequestIdRef = useRef(0)
  const assignInitialCheckedRef = useRef<Set<string>>(new Set())

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteDefaultOrgId, setInviteDefaultOrgId] = useState<string | undefined>()
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const inviteRequestIdRef = useRef(0)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const [editMember, setEditMember] = useState<Member | null>(null)
  const [editOrganization, setEditOrganization] = useState<Organization | null>(null)
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [memberRefreshKey, setMemberRefreshKey] = useState(0)
  const reloadInFlightRef = useRef<Promise<Organization[] | null> | null>(null)

  const reloadOrganizations = useCallback(async (options?: { showLoading?: boolean; notifyError?: boolean }) => {
    const showLoading = options?.showLoading ?? false
    const notifyError = options?.notifyError ?? showLoading
    if (reloadInFlightRef.current && !showLoading) {
      return reloadInFlightRef.current
    }

    const request = (async () => {
      if (showLoading) setLoadingOrganizations(true)
      try {
        const result = await fetchManagedOrganizations()
        setOrganizations(result.organizations)
        setCanCreateOrganization(result.canCreateOrganization)
        setOrgTab((prev) => resolveManagedOrgTab(prev, result.organizations))
        return result.organizations
      } catch (error) {
        if (notifyError) {
          message.error(error instanceof Error ? error.message : 'Failed to load organizations')
        }
        return null
      } finally {
        if (showLoading) setLoadingOrganizations(false)
      }
    })()

    reloadInFlightRef.current = request
    try {
      return await request
    } finally {
      if (reloadInFlightRef.current === request) {
        reloadInFlightRef.current = null
      }
    }
  }, [])

  const refreshMembers = () => setMemberRefreshKey((prev) => prev + 1)

  const refreshAfterMutation = async () => {
    refreshMembers()
    await reloadOrganizations({ notifyError: false })
  }

  const adminOrgs = useMemo(
    () => sortOrganizationsByName(organizations.filter((org) => org.canManageUsers)),
    [organizations],
  )

  const activeOrganization = useMemo(
    () => adminOrgs.find((org) => org.id === orgTab),
    [adminOrgs, orgTab],
  )

  const inviteableOrgs = useMemo(
    () => adminOrgs.filter(canAddUsersToOrganization),
    [adminOrgs],
  )

  const addUserDisabled = orgTab === 'all'
    ? inviteableOrgs.length === 0
    : !canAddUsersToOrganization(activeOrganization)

  const currentSessionOrganization = useMemo(
    () => organizations.find((org) => org.customerId === currentCustomerId || org.id === currentCustomerId),
    [organizations, currentCustomerId],
  )
  const currentSessionPlan = organizationPlan(currentSessionOrganization)

  const addUserHintOrganization = orgTab === 'all'
    ? currentSessionOrganization || adminOrgs[0]
    : activeOrganization

  const defaultInviteOrgId = inviteDefaultOrgId
    || (orgTab !== 'all' && canAddUsersToOrganization(activeOrganization) ? orgTab : inviteableOrgs[0]?.id)

  const resolveInviteOrgId = (orgs: Organization[], requested?: Organization) => {
    const adminList = sortOrganizationsByName(orgs.filter((org) => org.canManageUsers))
    const requestedOrg = requested
      ? adminList.find((org) => org.id === requested.id || org.customerId === requested.customerId)
      : orgTab !== 'all'
        ? adminList.find((org) => org.id === orgTab)
        : undefined
    if (requestedOrg && !canAddUsersToOrganization(requestedOrg)) {
      return { orgId: undefined as string | undefined, blocked: true }
    }
    const orgId = (requestedOrg && canAddUsersToOrganization(requestedOrg) ? requestedOrg.id : undefined)
      || adminList.find(canAddUsersToOrganization)?.id
    return { orgId, blocked: !orgId }
  }

  const openInvite = (organization?: Organization) => {
    const immediate = resolveInviteOrgId(organizations, organization)
    if (immediate.blocked) {
      message.warning('Users can only be added to Business or Enterprise organizations')
      return
    }

    const requestId = inviteRequestIdRef.current + 1
    inviteRequestIdRef.current = requestId
    if (immediate.orgId) setInviteDefaultOrgId(immediate.orgId)
    track(STATSIG.USER_MANAGEMENT.ADD_USER_BUTTON, 'click_open_popup', { type: 'click_open_popup' })
    setInviteOpen(true)
    setInviteLoading(true)

    void (async () => {
      const latest = await reloadOrganizations({ notifyError: true })
      if (inviteRequestIdRef.current !== requestId) return
      setInviteLoading(false)
      if (!latest) return
      const resolved = resolveInviteOrgId(latest, organization)
      if (resolved.blocked) {
        message.warning('Users can only be added to Business or Enterprise organizations')
        setInviteOpen(false)
        return
      }
      if (resolved.orgId) setInviteDefaultOrgId(resolved.orgId)
    })()
  }

  const openUpgrade = (organization: Organization) => {
    if (organization.customerId !== currentCustomerId && organization.id !== currentCustomerId) return
    setUpgradeModalOpen(true)
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuerySearch(search)
      setQueryFilters(filters)
    }, 100)
    return () => {
      window.clearTimeout(timer)
    }
  }, [filters, search])

  useEffect(() => {
    track(STATSIG.COMMON.VIEW, PAGE.USER_MANAGEMENT, { page: PAGE.USER_MANAGEMENT })
    void reloadOrganizations({ showLoading: true })
  }, [reloadOrganizations])

  const assignmentRolesFromRaw = (roles: string[] | undefined): OrgSelection['roles'] => {
    const rawRoles = roles || []
    const displayRoles: OrgSelection['roles'] = []
    if (rawRoles.includes('SuperAdmin')) displayRoles.push('Super Admin')
    if (rawRoles.includes('Owner')) displayRoles.push('Owner')
    if (rawRoles.includes('Underwriter')) displayRoles.push('Underwriter')
    if (rawRoles.includes('Closer')) displayRoles.push('Closer')
    if (rawRoles.includes('LoanProcessor')) displayRoles.push('Processor')
    if (rawRoles.includes('LoanOfficerManager')) displayRoles.push('LO Manager')
    if (rawRoles.includes('LoanOfficer')) displayRoles.push('Loan Officer')
    return displayRoles.length > 0 ? displayRoles : ['Loan Officer']
  }

  const buildSelectionsFromAssignments = (assignments: AssignmentRecord[], orgs: Organization[]) => {
    const assignmentByCustomer = new Map(assignments.map((assignment) => [assignment.customerId, assignment]))
    const nextSelections: Record<string, OrgSelection> = {}
    const existingRoles: Record<string, OrgRole[]> = {}
    orgs.forEach((org) => {
      const assignment = assignmentByCustomer.get(org.customerId)
      const roles = assignmentRolesFromRaw(assignment?.roles)
      nextSelections[org.id] = {
        checked: !!assignment?.checked,
        roles,
      }
      existingRoles[org.id] = roles
    })
    setAssignExistingRolesByOrg(existingRoles)
    return nextSelections
  }

  const rememberAssignChecked = (selections: Record<string, OrgSelection>) => {
    assignInitialCheckedRef.current = new Set(
      Object.entries(selections)
        .filter(([, selection]) => selection.checked)
        .map(([orgId]) => orgId),
    )
  }

  const openAssignModal = (member: Member, organization: Organization) => {
    const requestId = assignRequestIdRef.current + 1
    assignRequestIdRef.current = requestId
    setAssignMember(member)
    setAssignSourceOrganization(organization)
    setAssignLoading(true)
    const initialSelections = buildOrgSelections(adminOrgs, member.roles, undefined, member.roleLists)
    setAssignSelections(initialSelections)
    rememberAssignChecked(initialSelections)
    setAssignExistingRolesByOrg(
      Object.fromEntries(adminOrgs.map((org) => [org.id, initialSelections[org.id]?.roles || []])),
    )

    void (async () => {
      try {
        const [latest, assignments] = await Promise.all([
          reloadOrganizations({ notifyError: true }),
          fetchManagedOrganizationAssignments({
            customerId: organization.customerId,
            loanOfficerId: member.id,
          }),
        ])
        if (assignRequestIdRef.current !== requestId) return
        const latestAdmin = sortOrganizationsByName((latest || organizations).filter((org) => org.canManageUsers))
        const nextSelections = buildSelectionsFromAssignments(assignments, latestAdmin)
        setAssignSelections(nextSelections)
        rememberAssignChecked(nextSelections)
      } catch (error) {
        if (assignRequestIdRef.current !== requestId) return
        message.error(error instanceof Error ? error.message : 'Failed to load assignments')
        setAssignMember(null)
        setAssignSourceOrganization(null)
      } finally {
        if (assignRequestIdRef.current !== requestId) return
        setAssignLoading(false)
      }
    })()
  }

  const handleAssignSubmit = async () => {
    if (!assignMember || !assignSourceOrganization) return
    if (assignLoading) {
      message.warning('Assignments are still loading')
      return
    }
    const nextRoles = Object.fromEntries(
      Object.entries(rolesFromSelections(assignSelections)).filter(([orgId]) => {
        const org = adminOrgs.find((item) => item.id === orgId)
        if (hasSuperAdminRole(assignExistingRolesByOrg[orgId])) return true
        if (canAddUsersToOrganization(org)) return true
        return assignInitialCheckedRef.current.has(orgId)
      }),
    )
    Object.entries(assignExistingRolesByOrg).forEach(([orgId, roles]) => {
      if (!hasSuperAdminRole(roles)) return
      nextRoles[orgId] = assignSelections[orgId]?.roles || roles
    })
    setAssignSubmitting(true)
    try {
      const results = await assignManagedOrganizations({
        sourceCustomerId: assignSourceOrganization.customerId,
        sourceLoanOfficerId: assignMember.id,
        roles: nextRoles,
      })
      const failures = results.filter((result) => result.status === 'Error')
      await refreshAfterMutation()
      setAssignMember(null)
      setAssignSourceOrganization(null)
      setAssignExistingRolesByOrg({})
      if (failures.length > 0) {
        message.warning(`${results.length - failures.length} organizations updated, ${failures.length} failed`)
      } else {
        message.success('Organization access updated successfully')
      }
      track(STATSIG.USER_MANAGEMENT.ORGS_UPDATE)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to update assignments')
    } finally {
      setAssignSubmitting(false)
    }
  }

  const openEditModal = (member: Member, organization: Organization) => {
    setEditMember(member)
    setEditOrganization(organization)
  }

  const handleEditSubmit = async (payload: EditUserPayload) => {
    setEditSubmitting(true)
    try {
      const text = await updateManagedOrganizationLoanOfficer(payload)
      await refreshAfterMutation()
      setEditMember(null)
      setEditOrganization(null)
      if (text.includes('could not be re-sent')) {
        message.warning(text)
      } else {
        message.success(text)
      }
      track(STATSIG.USER_MANAGEMENT.UPDATE, payload.email, {
        email: payload.email,
        targetCustomerID: payload.customerId,
        nmls_number: payload.nmls,
      })
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to update user')
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleDisableUser = (member: Member, organization: Organization) => {
    Modal.confirm({
      title: 'Disable account?',
      content: (
        <div>
          <div>
            Are you sure you want to disable <strong>{member.name}</strong> in <strong>{organization.name}</strong>?
          </div>
          <div style={{ marginTop: 12 }}>
            Once disabled, the account will become inactive and no longer accessible to the user in this organization. Their loan data will remain archived and accessible.
          </div>
        </div>
      ),
      okText: 'Disable',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      async onOk() {
        try {
          await disableManagedOrganizationLoanOfficer({
            customerId: organization.customerId,
            loanOfficerId: member.id,
          })
          await refreshAfterMutation()
          message.success('Account disabled')
          track(STATSIG.USER_MANAGEMENT.STATUS, 'frozen', {
            status: 'frozen',
            targetUserAccount: member.email,
            targetCustomerID: organization.customerId,
          })
        } catch (error) {
          message.error(error instanceof Error ? error.message : 'Failed to disable user')
          throw error
        }
      },
    })
  }

  const handleResendInvite = async (member: Member, organization: Organization) => {
    try {
      await resendManagedOrganizationInvite({
        customerId: organization.customerId,
        loanOfficerId: member.id,
      })
      message.success(`Invitation sent to ${member.email}`)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to send the invitation')
      await refreshAfterMutation()
    }
  }

  const handleEnableUser = (member: Member, organization: Organization) => {
    Modal.confirm({
      title: 'Enable account?',
      content: (
        <div>
          <div>
            Enable <strong>{member.name}</strong> in <strong>{organization.name}</strong>?
          </div>
          <div style={{ marginTop: 12 }}>
            The user will regain access to this organization. If they have an active session, the organization will be available again from the branch switcher.
          </div>
        </div>
      ),
      okText: 'Enable',
      cancelText: 'Cancel',
      async onOk() {
        try {
          const text = await enableManagedOrganizationLoanOfficer({
            customerId: organization.customerId,
            loanOfficerId: member.id,
          })
          await refreshAfterMutation()
          message.success(text || 'Account enabled')
          track(STATSIG.USER_MANAGEMENT.STATUS, 'active', {
            status: 'active',
            targetUserAccount: member.email,
            targetCustomerID: organization.customerId,
          })
        } catch (error) {
          message.error(error instanceof Error ? error.message : 'Failed to enable user')
          throw error
        }
      },
    })
  }

  const handleInviteSubmit = async (payload: InvitePayload) => {
    if (inviteLoading) {
      message.warning('Organizations are still loading')
      return []
    }
    const selectedOrgs = Object.keys(payload.roles).filter((orgId) => {
      const org = adminOrgs.find((item) => item.id === orgId)
      return canAddUsersToOrganization(org)
    })
    if (selectedOrgs.length === 0) {
      message.warning('Select at least one organization')
      return []
    }
    if (!payload.email) {
      message.warning('Email is required')
      return []
    }

    setInviteSubmitting(true)
    try {
      const invitePayload = {
        ...payload,
        roles: Object.fromEntries(selectedOrgs.map((orgId) => [orgId, payload.roles[orgId]])),
      }
      const checkResults = await checkManagedOrganizationInvite(invitePayload)
      const duplicateResults = checkResults.filter((result) => result.exists)
      const blockingErrors = checkResults.filter((result) => result.status === 'Error')
      const remainingOrgIds = selectedOrgs.filter((orgId) => {
        const result = checkResults.find((item) => item.customerId === orgId)
        return result?.status === 'OK' && !result.exists
      })

      if (remainingOrgIds.length === 0) {
        const visibleErrors = [
          ...blockingErrors,
          ...duplicateResults.filter((item) => !blockingErrors.some((error) => error.customerId === item.customerId)),
        ]
        if (visibleErrors.length > 0) {
          message.error(
            <span style={{ whiteSpace: 'pre-line' }}>{formatInviteFailureMessage(visibleErrors, adminOrgs)}</span>,
            8,
          )
          return visibleErrors
        }
        message.warning('Select at least one organization')
        return []
      }

      const inviteResults = await sendManagedOrganizationInvite({
        ...payload,
        roles: Object.fromEntries(remainingOrgIds.map((orgId) => [orgId, payload.roles[orgId]])),
      })
      const failedResults = inviteResults.filter((result) => result.status === 'Error')
      const successfulResults = inviteResults.filter((result) => result.status !== 'Error' && !result.exists)
      if (successfulResults.length > 0) {
        await refreshAfterMutation()
        track(STATSIG.USER_MANAGEMENT.INSERT, payload.email, {
          email: payload.email,
          first_name: payload.firstName,
          last_name: payload.lastName,
          nmls_number: payload.nmls,
          targetCustomerIDs: remainingOrgIds,
        })
        try {
          await sendSignupToHubSpot({
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
            company: await resolveHubSpotCompanyAsync(subscription),
            leadSource: 'Admin_Entry_sign_up',
            utmParams: {},
            assignOwner: false,
          })
        } catch (hubSpotError) {
          console.warn('HubSpot signup sync failed:', hubSpotError)
        }
      }

      const visibleErrors = [
        ...blockingErrors,
        ...failedResults.filter((item) => !blockingErrors.some((error) => error.customerId === item.customerId)),
      ]
      if (visibleErrors.length > 0) {
        message.error(
          <span style={{ whiteSpace: 'pre-line' }}>{formatInviteFailureMessage(visibleErrors, adminOrgs)}</span>,
          8,
        )
        if (successfulResults.length > 0) {
          message.success('User added successfully')
        }
        return [...visibleErrors, ...inviteResults.filter((item) => !visibleErrors.some((error) => error.customerId === item.customerId))]
      }

      setInviteOpen(false)
      message.success('User added successfully')
      return inviteResults
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to send invite')
      return []
    } finally {
      setInviteSubmitting(false)
    }
  }

  const handleOrganizationCreated = async ({ organization }: NewOrganizationResult) => {
    const latest = await reloadOrganizations({ notifyError: false })
    if (latest?.some((org) => org.id === organization.id || org.customerId === organization.customerId)) {
      setOrgTab(organization.id)
      return
    }
    setOrganizations((prev) => (
      prev.some((org) => org.id === organization.id) ? prev : [...prev, organization]
    ))
    setOrgTab(organization.id)
  }

  return (
    <div className="um-refactor">
      <PageHeader />
      <OrgTabs
        adminOrgs={adminOrgs}
        activeTab={orgTab}
        orgCount={organizations.length}
        canCreateOrganization={canCreateOrganization}
        sourcePlan={currentSessionPlan}
        onTabChange={setOrgTab}
        onOrganizationCreated={handleOrganizationCreated}
      />
      <Toolbar
        search={search}
        orgCount={adminOrgs.length}
        addUserDisabled={addUserDisabled}
        addUserHintOrganization={addUserHintOrganization}
        currentCustomerId={currentCustomerId}
        onSearchChange={setSearch}
        onAddUser={() => { void openInvite() }}
        onUpgrade={openUpgrade}
      />
      <div className="um-refactor__content">
        {loadingOrganizations ? (
          <MemberTableState loading />
        ) : adminOrgs.length === 0 ? (
          <MemberTableState description="No organizations to manage" />
        ) : orgTab === 'all' && adminOrgs.length > 1 ? (
          <AllOrganizationsMembers
            key="all"
            organizations={adminOrgs}
            search={querySearch}
            filters={queryFilters}
            filterSelection={filters}
            onFiltersChange={setFilters}
            refreshKey={memberRefreshKey}
            currentCustomerId={currentCustomerId}
            onInvite={openInvite}
            onAssign={organizations.length > 1 ? openAssignModal : undefined}
            onEdit={openEditModal}
            onDisable={handleDisableUser}
            onEnable={handleEnableUser}
            onResendInvite={handleResendInvite}
            onUpgrade={openUpgrade}
          />
        ) : (
          <OrganizationMembers
            key={activeOrganization?.id || adminOrgs[0].id}
            organization={activeOrganization || adminOrgs[0]}
            organizations={adminOrgs}
            search={querySearch}
            filters={queryFilters}
            filterSelection={filters}
            onFiltersChange={setFilters}
            refreshKey={memberRefreshKey}
            currentCustomerId={currentCustomerId}
            onInvite={openInvite}
            onAssign={organizations.length > 1 ? openAssignModal : undefined}
            onEdit={openEditModal}
            onDisable={handleDisableUser}
            onEnable={handleEnableUser}
            onResendInvite={handleResendInvite}
            onUpgrade={openUpgrade}
          />
        )}
      </div>

      <AssignOrgModal
        open={!!assignMember}
        member={assignMember}
        organizations={adminOrgs}
        selections={assignSelections}
        loading={assignLoading}
        submitting={assignSubmitting}
        currentCustomerId={currentCustomerId}
        existingRolesByOrgId={assignExistingRolesByOrg}
        onSelectionChange={(orgId, patch) => {
          const org = adminOrgs.find((item) => item.id === orgId)
          if (patch.checked === false && hasSuperAdminRole(assignExistingRolesByOrg[orgId])) {
            return
          }
          if (patch.checked && org && !canAddUsersToOrganization(org) && !assignSelections[orgId]?.checked) {
            return
          }
          setAssignSelections((prev) => ({ ...prev, [orgId]: { ...prev[orgId], ...patch } }))
        }}
        onCancel={() => {
          assignRequestIdRef.current += 1
          setAssignMember(null)
          setAssignSourceOrganization(null)
          setAssignExistingRolesByOrg({})
          setAssignLoading(false)
        }}
        onSubmit={handleAssignSubmit}
        onUpgrade={openUpgrade}
      />

      <InviteUserModal
        open={inviteOpen}
        organizations={adminOrgs}
        defaultCheckedOrgId={defaultInviteOrgId}
        loading={inviteLoading}
        submitting={inviteSubmitting}
        currentCustomerId={currentCustomerId}
        onCancel={() => {
          inviteRequestIdRef.current += 1
          setInviteOpen(false)
          setInviteLoading(false)
        }}
        onSubmit={handleInviteSubmit}
        onUpgrade={openUpgrade}
      />

      <EditUserModal
        open={!!editMember && !!editOrganization}
        member={editMember}
        organization={editOrganization}
        submitting={editSubmitting}
        onCancel={() => {
          setEditMember(null)
          setEditOrganization(null)
        }}
        onSubmit={handleEditSubmit}
      />

      <UpgradeToBusinessModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onUpgrade={() => {
          setUpgradeModalOpen(false)
          toPricing?.()
        }}
        onViewPlanDetail={() => {}}
      />
    </div>
  )
}

export default UserManagement
